import { DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { LoadingBoxComponent } from '../../../../shared/components/loading-box/loading-box.component';
import { AutocompletePesquisavelComponent } from '../../../../shared/components/autocomplete-pesquisavel/autocomplete-pesquisavel.component';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { TipoContatoEnum } from '../../../../shared/enums/pessoa/tipo-contato.enum';
import { AbntPipe } from '../../../../shared/pipes/abnt.pipe';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { RelatorioEntradasSaidasClienteService } from '../../../../shared/services/apis/financas/relatorio-entradas-saidas-cliente.service';
import { PessoaLookupService } from '../../../../shared/services/apis/pessoa/pessoa-lookup.service';
import { AuthService } from '../../../../shared/services/apis/usuario/auth.service';
import { MaskService } from '../../../../shared/services/mask.service';
import { RelatorioEntradasSaidasClienteRequest } from '../../../../shared/services/requests/financas/relatorio-entradas-saidas-cliente.request';
import { SaveRelatorioFiltroFavoritoRequest } from '../../../../shared/services/requests/financas/save-relatorio-filtro-favorito.request';
import { ListPessoaIdsRequest } from '../../../../shared/services/requests/pessoa/list-pessoa-ids-request';
import { RelatorioFiltroFavorito } from '../../../../shared/services/responses/financas/relatorio-filtro-favorito.response';
import { RelatorioEntradasSaidasClienteGrupo } from '../../../../shared/services/responses/financas/relatorio-entradas-saidas-cliente.response';
import { LookupItemStruct } from '../../../../shared/services/structs/pessoa/lookup-item.struct';
import { environment } from '../../../../../environments/environment';
import { NomeFavoritoModalComponent } from './components/nome-favorito-modal/nome-favorito-modal.component';

type ModoPeriodo = 'mes' | 'intervalo';

interface ContaOption {
  id: number;
  descricao: string;
}

interface CategoriaOption {
  id: number;
  descricao: string;
}

interface ClienteResumoGrupo {
  clienteId: number;
  clienteNome: string;
  saldoAnterior: number;
  totalPeriodo: number;
  saldoFinal: number;
  movimentacoes: RelatorioMovimento[];
}

interface RelatorioMovimento {
  id: number;
  data: string;
  historico: string;
  categoria: string;
  valor: number;
}

@Component({
  selector: 'app-relatorio-entradas-saidas-cliente',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterModule,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatTooltipModule,
    HeaderComponent,
    LoadingBoxComponent,
    AutocompletePesquisavelComponent,
    AbntPipe,
  ],
  providers: [DatePipe],
  templateUrl: './relatorio-entradas-saidas-cliente.component.html',
  styleUrl: './relatorio-entradas-saidas-cliente.component.css',
})
export class RelatorioEntradasSaidasClienteComponent implements OnInit, OnDestroy {
  public moduloEnum = ModuloEnum.Financas;
  public funcionalidadeEnum = FuncionalidadeEnum.Financas_Relatorios;

  public readonly urlPessoaLookup = `${environment.urlApiPessoa}Lookup/listPessoaSelect?idTipoContato=${TipoContatoEnum.Cliente}`;
  public readonly periodoOpcoes = [
    { id: 'mes' as const, descricao: 'do mês' },
    { id: 'intervalo' as const, descricao: 'intervalo' },
  ];

  public form = this.fb.group({
    modoPeriodo: ['mes' as ModoPeriodo, Validators.required],
    mesAno: [this.getMesAnoAtual(), Validators.required],
    dataInicial: [null as Date | null],
    dataFinal: [null as Date | null],
    idCliente: [null as number | null],
    idContaFinanceira: [null as number | null],
    idCategoriaFinanceira: [null as number | null],
    favoritoSelecionado: [null as string | null],
  });

  public carregandoLookups = true;
  public carregandoRelatorio = false;
  public carregandoClienteLookup = false;
  public filtrosVisiveis = true;
  public consultaGerada = false;
  public possuiResultados = false;
  public mensagemEmptyState = '';
  public clienteSelecionado: LookupItemStruct | null = null;

  public contas: ContaOption[] = [];
  public categorias: CategoriaOption[] = [];
  public favoritos: RelatorioFiltroFavorito[] = [];
  public grupos: ClienteResumoGrupo[] = [];
  public totalGeralPeriodo = 0;
  public totalGeralSaldoFinal = 0;

  private readonly destroy$ = new Subject<void>();
  private readonly codigoRelatorio = 'ENTRADAS_SAIDAS_CLIENTE';
  private readonly favoritosStorageKey = `financas-${this.codigoRelatorio.toLowerCase()}-favoritos`;
  private readonly idTipoRelatorioState = Number(window.history.state?.idTipoRelatorio || 0) || null;
  private idTipoRelatorio: number | null = null;

  constructor(
    private fb: FormBuilder,
    private datePipe: DatePipe,
    private alertService: AlertService,
    private relatorioEntradasSaidasClienteService: RelatorioEntradasSaidasClienteService,
    private pessoaLookupService: PessoaLookupService,
    private router: Router,
    private dialog: MatDialog,
    private authService: AuthService
  ) {}

  public voltar(): void {
    this.router.navigate(['/financas/relatorios']);
  }

  public ngOnInit(): void {
    this.carregarLookups();
    this.carregarFavoritos();
    this.configurarMudancaPeriodo();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public get idClienteControl(): FormControl<number | null> {
    return this.form.get('idCliente') as FormControl<number | null>;
  }

  public get isModoMes(): boolean {
    return this.form.get('modoPeriodo')?.value === 'mes';
  }

  public get mesAnoControl(): FormControl<string> {
    return this.form.get('mesAno') as FormControl<string>;
  }

  public get dataInicialControl(): FormControl<Date | null> {
    return this.form.get('dataInicial') as FormControl<Date | null>;
  }

  public get dataFinalControl(): FormControl<Date | null> {
    return this.form.get('dataFinal') as FormControl<Date | null>;
  }

  public carregarLookups(): void {
    this.carregandoLookups = true;

    this.relatorioEntradasSaidasClienteService
      .getLookup()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.contas = (response.listaContaFinanceira || [])
            .map(item => ({
              id: Number(item.id),
              descricao: item.descricao,
            }))
            .sort((a, b) => a.descricao.localeCompare(b.descricao));

          this.categorias = (response.listaCategoriaFinanceira || [])
            .map(item => ({
              id: Number(item.id),
              descricao: item.descricao,
            }))
            .sort((a, b) => a.descricao.localeCompare(b.descricao));

          this.idTipoRelatorio = response.idTipoRelatorio ?? this.idTipoRelatorioState;
          this.favoritos = response.listaFavoritos || [];
          if (!this.idTipoRelatorio) {
            this.carregarFavoritosLocalStorage();
          }

          this.carregandoLookups = false;
        },
        error: () => {
          this.carregandoLookups = false;
          this.alertService.show('Erro', 'Erro ao carregar os filtros do relatório.', AlertType.error);
        },
      });
  }

  public onClienteSelecionado(item: LookupItemStruct): void {
    this.clienteSelecionado = item;
  }

  public selecionarModoPeriodo(modo: ModoPeriodo): void {
    this.form.patchValue({ modoPeriodo: modo });
  }

  public onDateInput(event: Event, controlName: 'dataInicial' | 'dataFinal'): void {
    MaskService.onDateInput(event, this.form.get(controlName));
  }

  public navegarMes(passos: number): void {
    const referencia = this.parseMesAno(this.mesAnoControl.value) ?? new Date();
    referencia.setMonth(referencia.getMonth() + passos);
    this.mesAnoControl.setValue(this.formatMesAno(referencia));
  }

  public normalizarMesAno(): void {
    const normalizado = this.parseMesAno(this.mesAnoControl.value);
    if (!normalizado) {
      this.mesAnoControl.setValue(this.getMesAnoAtual());
      return;
    }

    this.mesAnoControl.setValue(this.formatMesAno(normalizado));
  }

  public favoritarFiltros(): void {
    if (this.hasFavoritoSelecionado) {
      this.removerFavoritoSelecionado();
      return;
    }

    const dialogRef = this.dialog.open(NomeFavoritoModalComponent, {
      width: '480px',
      maxWidth: '95vw',
      autoFocus: false,
      data: {
        nomeInicial: this.getProximoNomeFavorito(),
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((nome: string | undefined) => {
        if (!nome?.trim()) {
          return;
        }

        this.salvarFavorito(nome.trim());
      });
  }

  public carregarFavoritoSelecionado(): void {
    const favoritoId = this.form.get('favoritoSelecionado')?.value;
    if (!favoritoId) {
      this.limparFiltrosFavorito();
      return;
    }

    const favorito = this.favoritos.find(item => String(item.idRelatorioFiltroFavorito) === favoritoId);
    if (!favorito) {
      this.limparFiltrosFavorito();
      return;
    }

    this.form.patchValue({
      modoPeriodo: (favorito.filtro.modoPeriodo === 'intervalo' ? 'intervalo' : 'mes') as ModoPeriodo,
      mesAno: favorito.filtro.mesAno,
      dataInicial: favorito.filtro.dataInicial ? new Date(favorito.filtro.dataInicial) : null,
      dataFinal: favorito.filtro.dataFinal ? new Date(favorito.filtro.dataFinal) : null,
      idCliente: favorito.filtro.idCliente,
      idContaFinanceira: favorito.filtro.idContaFinanceira,
      idCategoriaFinanceira: favorito.filtro.idCategoriaFinanceira,
    });

    this.clienteSelecionado = favorito.filtro.idCliente
      ? { id: favorito.filtro.idCliente, descricao: favorito.filtro.clienteDescricao || '', codigo: '' }
      : null;
  }

  private limparFiltrosFavorito(): void {
    this.form.patchValue({
      favoritoSelecionado: null,
      modoPeriodo: 'mes',
      mesAno: this.getMesAnoAtual(),
      dataInicial: null,
      dataFinal: null,
      idCliente: null,
      idContaFinanceira: null,
      idCategoriaFinanceira: null,
    });
    this.clienteSelecionado = null;
  }

  public gerar(): void {
    if (!this.validarPeriodo()) {
      return;
    }

    const { inicio, fim } = this.getPeriodoSelecionado();
    if (!inicio || !fim) {
      this.alertService.show(
        'Atenção',
        'Informe um período válido para gerar o relatório.',
        AlertType.warning
      );
      return;
    }

    this.consultaGerada = true;
    this.mensagemEmptyState = '';

    const request: RelatorioEntradasSaidasClienteRequest = {
      dataInicio: this.datePipe.transform(inicio, 'yyyy-MM-dd') || '',
      dataFim: this.datePipe.transform(fim, 'yyyy-MM-dd') || '',
      idCliente: this.form.get('idCliente')?.value ?? null,
      idContaFinanceira: this.form.get('idContaFinanceira')?.value ?? null,
      idCategoriaFinanceira: this.form.get('idCategoriaFinanceira')?.value ?? null,
    };

    this.carregandoRelatorio = true;
    this.relatorioEntradasSaidasClienteService
      .getPreview(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.grupos = (response.grupos || []).map(item => this.mapGrupo(item));
          this.totalGeralPeriodo = Number(response.totalGeralPeriodo || 0);
          this.totalGeralSaldoFinal = Number(response.totalGeralSaldoFinal || 0);
          this.possuiResultados = this.grupos.length > 0;
          this.filtrosVisiveis = !this.possuiResultados;
          this.mensagemEmptyState = this.possuiResultados
            ? ''
            : 'A consulta não retornou nenhum registro. Tente refazer a consulta com outros parâmetros.';
          this.aplicarNomesClientes();
        },
        error: () => {
          this.grupos = [];
          this.totalGeralPeriodo = 0;
          this.totalGeralSaldoFinal = 0;
          this.possuiResultados = false;
          this.filtrosVisiveis = true;
          this.carregandoRelatorio = false;
          this.alertService.show('Erro', 'Erro ao gerar o relatório.', AlertType.error);
        },
      });
  }

  public exibirFiltros(): void {
    this.filtrosVisiveis = true;
  }

  public toggleFiltros(): void {
    this.filtrosVisiveis = !this.filtrosVisiveis;

    if (this.filtrosVisiveis) {
      this.restaurarClienteSelecionado();
    }
  }

  private restaurarClienteSelecionado(): void {
    const idCliente = this.form.get('idCliente')?.value ?? null;
    if (!idCliente || idCliente <= 0) {
      return;
    }

    // Se já temos o objeto, o autocomplete consegue renderizar normalmente.
    if (this.clienteSelecionado?.id === idCliente && !!this.clienteSelecionado?.descricao?.trim()) {
      return;
    }

    const request = new ListPessoaIdsRequest();
    request.listIdPessoa = [idCliente];
    this.carregandoClienteLookup = true;

    this.pessoaLookupService
      .getListPessoaIds(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (response.isError) {
            this.carregandoClienteLookup = false;
            return;
          }

          const pessoa = (response.listaPessoa || []).find(item => item.idPessoa === idCliente);
          const nome = pessoa?.nome?.trim();
          if (!nome) {
            this.carregandoClienteLookup = false;
            return;
          }

          this.clienteSelecionado = { id: idCliente, descricao: nome, codigo: '' };
          this.carregandoClienteLookup = false;
        },
        error: () => {
          this.carregandoClienteLookup = false;
        },
      });
  }

  public imprimir(): void {
    if (!this.possuiResultados) {
      return;
    }

    const periodo = this.getPeriodoDescricao();
    const empresa = this.authService.getUserInfos().nomeEmpresa;
    const geradoEm = this.datePipe.transform(new Date(), 'dd/MM/yyyy HH:mm') || '';
    const conteudo = `
            <html>
            <head>
                <title>Relatório de Entrada/Saída por Cliente</title>
                <style>
                    body { font-family: Arial, sans-serif; color: #1f2937; padding: 24px; }
                    h1 { margin: 0 0 8px; font-size: 22px; }
                    .meta { margin-bottom: 24px; color: #475569; font-size: 12px; }
                    .grupo { margin-bottom: 28px; page-break-inside: avoid; }
                    .grupo h2 { font-size: 18px; margin: 0 0 12px; }
                    table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
                    thead { display: table-header-group; }
                    th, td { border-bottom: 1px solid #e5e7eb; padding: 10px 8px; text-align: left; }
                    th.valor, td.valor { text-align: right; }
                    .resumo td { font-weight: 600; }
                    .total-geral { margin-top: 20px; font-weight: 700; font-size: 16px; text-align: right; }
                </style>
            </head>
            <body>
                <h1>Relatório de Entrada/Saída por Cliente</h1>
                <div class="meta">
                    <div>Período: ${periodo}</div>
                    <div>Gerado em: ${geradoEm}</div>
                    <div>Empresa: ${empresa}</div>
                </div>
                ${this.grupos
                  .map(
                    grupo => `
                    <section class="grupo">
                        <h2>Cliente: ${grupo.clienteNome}</h2>
                        <table>
                            <thead>
                                <tr>
                                    <th>Data</th>
                                    <th>Histórico</th>
                                    <th>Categoria</th>
                                    <th class="valor">Valor</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${grupo.movimentacoes
                                  .map(
                                    item => `
                                    <tr>
                                        <td>${this.formatarData(item.data)}</td>
                                        <td>${item.historico}</td>
                                        <td>${item.categoria}</td>
                                        <td class="valor">${this.formatarMoeda(item.valor)}</td>
                                    </tr>
                                `
                                  )
                                  .join('')}
                            </tbody>
                        </table>
                        <table class="resumo">
                            <tbody>
                                <tr><td>Saldo anterior</td><td class="valor">${this.formatarMoeda(grupo.saldoAnterior)}</td></tr>
                                <tr><td>Total do período</td><td class="valor">${this.formatarMoeda(grupo.totalPeriodo)}</td></tr>
                                <tr><td>Saldo final</td><td class="valor">${this.formatarMoeda(grupo.saldoFinal)}</td></tr>
                            </tbody>
                        </table>
                    </section>
                `
                  )
                  .join('')}
                <div class="total-geral">Total Geral do Relatório: ${this.formatarMoeda(this.totalGeralSaldoFinal)}</div>
            </body>
            </html>
        `;

    const popup = window.open('', '_blank', 'width=1200,height=900');
    if (!popup) {
      return;
    }

    popup.document.write(conteudo);
    popup.document.close();
    popup.focus();
    popup.print();
  }

  public downloadCsv(): void {
    if (!this.possuiResultados) {
      return;
    }

    const linhas = [
      ['Contato', 'Data', 'Histórico', 'Categoria', 'Valor'],
      ...this.grupos.flatMap(grupo =>
        grupo.movimentacoes.map(item => [
          grupo.clienteNome,
          this.formatarData(item.data),
          item.historico,
          item.categoria,
          item.valor.toFixed(2),
        ])
      ),
    ];

    const csv = linhas
      .map(linha => linha.map(valor => `"${String(valor).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'relatorio-entradas-saidas-por-cliente.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  }

  public limparCliente(): void {
    this.form.patchValue({ idCliente: null });
    this.clienteSelecionado = null;
  }

  public getPeriodoDescricao(): string {
    if (this.isModoMes) {
      return this.mesAnoControl.value;
    }

    const inicio = this.dataInicialControl.value
      ? this.datePipe.transform(this.dataInicialControl.value, 'dd/MM/yyyy')
      : '';
    const fim = this.dataFinalControl.value
      ? this.datePipe.transform(this.dataFinalControl.value, 'dd/MM/yyyy')
      : '';
    return `${inicio} a ${fim}`;
  }

  private configurarMudancaPeriodo(): void {
    this.form
      .get('modoPeriodo')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(modo => {
        if (!modo) {
          return;
        }

        if (modo === 'mes') {
          this.dataInicialControl.setValue(null);
          this.dataFinalControl.setValue(null);
        } else {
          this.mesAnoControl.setValue(this.getMesAnoAtual());
        }
      });
  }

  private mapGrupo(item: RelatorioEntradasSaidasClienteGrupo): ClienteResumoGrupo {
    return {
      clienteId: Number(item.idCliente),
      clienteNome: item.cliente,
      saldoAnterior: Number(item.saldoAnterior || 0),
      totalPeriodo: Number(item.totalPeriodo || 0),
      saldoFinal: Number(item.saldoFinal || 0),
      movimentacoes: (item.movimentacoes || []).map((movimentacao, index) => ({
        id: index + 1,
        data: movimentacao.data,
        historico: movimentacao.historico,
        categoria: movimentacao.categoria,
        valor: Number(movimentacao.valor || 0),
      })),
    };
  }

  private aplicarNomesClientes(): void {
    const idsClientes = Array.from(
      new Set(this.grupos.map(item => item.clienteId).filter(id => !!id && id > 0))
    );

    if (!idsClientes.length) {
      this.grupos = this.grupos.map(item => ({
        ...item,
        clienteNome: item.clienteId > 0 ? item.clienteNome : 'Sem cliente',
      }));
      this.carregandoRelatorio = false;
      return;
    }

    const request = new ListPessoaIdsRequest();
    request.listIdPessoa = idsClientes;

    this.pessoaLookupService
      .getListPessoaIds(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (response.isError) {
            this.grupos = this.grupos.map(item => ({
              ...item,
              clienteNome: item.clienteId > 0 ? item.clienteNome : 'Sem cliente',
            }));
            this.carregandoRelatorio = false;
            return;
          }

          const nomesPorId = new Map(
            (response.listaPessoa || [])
              .filter(item => !!item.idPessoa && !!item.nome?.trim())
              .map(item => [item.idPessoa, item.nome.trim()])
          );

          this.grupos = this.grupos.map(item => ({
            ...item,
            clienteNome:
              item.clienteId > 0 ? nomesPorId.get(item.clienteId) || item.clienteNome : 'Sem cliente',
          }));
          this.carregandoRelatorio = false;
        },
        error: () => {
          this.grupos = this.grupos.map(item => ({
            ...item,
            clienteNome: item.clienteId > 0 ? item.clienteNome : 'Sem cliente',
          }));
          this.carregandoRelatorio = false;
        },
      });
  }

  private validarPeriodo(): boolean {
    if (this.isModoMes) {
      const mesAno = this.parseMesAno(this.mesAnoControl.value);
      if (!mesAno) {
        this.alertService.show('Atenção', 'Informe um mês/ano válido no formato MM/AAAA.', AlertType.warning);
        return false;
      }

      return true;
    }

    const dataInicial = this.dataInicialControl.value;
    const dataFinal = this.dataFinalControl.value;

    if (!dataInicial || !dataFinal) {
      this.alertService.show('Atenção', 'Informe a data inicial e a data final.', AlertType.warning);
      return false;
    }

    if (dataFinal < dataInicial) {
      this.alertService.show(
        'Atenção',
        'A data final não pode ser anterior à data inicial.',
        AlertType.warning
      );
      return false;
    }

    return true;
  }

  private getPeriodoSelecionado(): { inicio: Date | null; fim: Date | null } {
    if (this.isModoMes) {
      const referencia = this.parseMesAno(this.mesAnoControl.value);
      if (!referencia) {
        return { inicio: null, fim: null };
      }

      const inicio = new Date(referencia.getFullYear(), referencia.getMonth(), 1);
      const fim = new Date(referencia.getFullYear(), referencia.getMonth() + 1, 0);
      fim.setHours(23, 59, 59, 999);
      return { inicio, fim };
    }

    const inicio = this.dataInicialControl.value ? new Date(this.dataInicialControl.value) : null;
    const fim = this.dataFinalControl.value ? new Date(this.dataFinalControl.value) : null;
    if (fim) {
      fim.setHours(23, 59, 59, 999);
    }

    return { inicio, fim };
  }

  private carregarFavoritos(): void {
    if (this.idTipoRelatorio) {
      return;
    }
    this.carregarFavoritosLocalStorage();
  }

  private carregarFavoritosLocalStorage(): void {
    try {
      const favoritosStorage = localStorage.getItem(this.favoritosStorageKey);
      this.favoritos = favoritosStorage ? (JSON.parse(favoritosStorage) as RelatorioFiltroFavorito[]) : [];
    } catch {
      this.favoritos = [];
    }
  }

  private persistirFavoritos(): void {
    if (this.idTipoRelatorio) {
      return;
    }
    localStorage.setItem(this.favoritosStorageKey, JSON.stringify(this.favoritos));
  }

  private getFiltroAtual(): SaveRelatorioFiltroFavoritoRequest['filtro'] {
    return {
      modoPeriodo: this.form.get('modoPeriodo')?.value || 'mes',
      mesAno: this.mesAnoControl.value,
      dataInicial: this.dataInicialControl.value
        ? this.datePipe.transform(this.dataInicialControl.value, 'yyyy-MM-dd')
        : null,
      dataFinal: this.dataFinalControl.value
        ? this.datePipe.transform(this.dataFinalControl.value, 'yyyy-MM-dd')
        : null,
      idCliente: this.form.get('idCliente')?.value ?? null,
      clienteDescricao: this.clienteSelecionado?.descricao ?? null,
      idContaFinanceira: this.form.get('idContaFinanceira')?.value ?? null,
      idCategoriaFinanceira: this.form.get('idCategoriaFinanceira')?.value ?? null,
    };
  }

  private getMesAnoAtual(): string {
    return this.formatMesAno(new Date());
  }

  private formatMesAno(date: Date): string {
    const mes = `${date.getMonth() + 1}`.padStart(2, '0');
    return `${mes}/${date.getFullYear()}`;
  }

  private parseMesAno(value: string | null | undefined): Date | null {
    const match = (value || '').trim().match(/^(\d{2})\/(\d{4})$/);
    if (!match) {
      return null;
    }

    const mes = Number(match[1]);
    const ano = Number(match[2]);
    if (mes < 1 || mes > 12) {
      return null;
    }

    return new Date(ano, mes - 1, 1);
  }

  public formatarData(value: string): string {
    const normalizado = value.includes('T') ? value : `${value}T00:00:00`;
    return this.datePipe.transform(normalizado, 'dd/MM/yyyy') || '';
  }

  public formatarMoeda(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  }

  public getFavoritoValue(favorito: RelatorioFiltroFavorito): string {
    return String(favorito.idRelatorioFiltroFavorito);
  }

  public get hasFavoritoSelecionado(): boolean {
    return !!this.favoritoSelecionadoAtual;
  }

  public get hasAlgumFiltroPreenchido(): boolean {
    const idCliente = this.form.get('idCliente')?.value ?? null;
    const idContaFinanceira = this.form.get('idContaFinanceira')?.value ?? null;
    const idCategoriaFinanceira = this.form.get('idCategoriaFinanceira')?.value ?? null;

    if (idCliente || idContaFinanceira || idCategoriaFinanceira) {
      return true;
    }

    // Período é obrigatório; só conta como "preenchido" quando intervalo tem datas informadas.
    if (this.form.get('modoPeriodo')?.value === 'intervalo') {
      const dataInicial = this.form.get('dataInicial')?.value ?? null;
      const dataFinal = this.form.get('dataFinal')?.value ?? null;
      return !!dataInicial || !!dataFinal;
    }

    return false;
  }

  public get favoritoSelecionadoAtual(): RelatorioFiltroFavorito | null {
    const favoritoId = this.form.get('favoritoSelecionado')?.value;
    return this.favoritos.find(item => String(item.idRelatorioFiltroFavorito) === favoritoId) || null;
  }

  private removerFavoritoSelecionado(): void {
    const favorito = this.favoritoSelecionadoAtual;
    if (!favorito) {
      return;
    }

    if (!this.idTipoRelatorio) {
      this.favoritos = this.favoritos.filter(
        item => item.idRelatorioFiltroFavorito !== favorito.idRelatorioFiltroFavorito
      );
      this.persistirFavoritos();
      this.form.patchValue({ favoritoSelecionado: null });
      return;
    }

    this.relatorioEntradasSaidasClienteService
      .deleteFavorito(favorito.idRelatorioFiltroFavorito)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (response.isError) {
            this.alertService.show(
              'Erro',
              response.errorDescription || 'Erro ao remover favorito.',
              AlertType.error
            );
            return;
          }

          this.favoritos = this.favoritos.filter(
            item => item.idRelatorioFiltroFavorito !== favorito.idRelatorioFiltroFavorito
          );
          this.form.patchValue({ favoritoSelecionado: null });
        },
        error: () => {
          this.alertService.show('Erro', 'Erro ao remover favorito.', AlertType.error);
        },
      });
  }

  private getProximoNomeFavorito(): string {
    const numeros = this.favoritos
      .map(item => /^Favorito (\d+)$/.exec(item.nome)?.[1])
      .filter((value): value is string => !!value)
      .map(Number)
      .filter(value => !Number.isNaN(value));

    const proximoNumero = numeros.length ? Math.max(...numeros) + 1 : this.favoritos.length + 1;
    return `Favorito ${proximoNumero}`;
  }

  private salvarFavorito(nome: string): void {
    const filtro = this.getFiltroAtual();

    if (!this.idTipoRelatorio) {
      const favorito = {
        idRelatorioFiltroFavorito: Date.now(),
        idTipoRelatorio: 0,
        nome,
        isPadrao: false,
        datetimeInclusao: new Date().toISOString(),
        filtro,
      } as RelatorioFiltroFavorito;

      this.favoritos = [favorito, ...this.favoritos];
      this.persistirFavoritos();
      this.form.patchValue({ favoritoSelecionado: String(favorito.idRelatorioFiltroFavorito) });
      return;
    }

    const request: SaveRelatorioFiltroFavoritoRequest = {
      idTipoRelatorio: this.idTipoRelatorio,
      nome,
      isPadrao: false,
      filtro,
    };

    this.relatorioEntradasSaidasClienteService
      .saveFavorito(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (response.isError || !response.favorito) {
            this.alertService.show(
              'Erro',
              response.errorDescription || 'Erro ao salvar favorito.',
              AlertType.error
            );
            return;
          }

          const favorito = response.favorito;
          this.favoritos = [
            favorito,
            ...this.favoritos.filter(
              item => item.idRelatorioFiltroFavorito !== favorito.idRelatorioFiltroFavorito
            ),
          ].sort((a, b) => a.nome.localeCompare(b.nome));

          this.form.patchValue({ favoritoSelecionado: String(favorito.idRelatorioFiltroFavorito) });
        },
        error: () => {
          this.alertService.show('Erro', 'Erro ao salvar favorito.', AlertType.error);
        },
      });
  }
}
